import {
  createRouter,
  type LocationRewrite,
} from "@tanstack/react-router";
import { deLocalizeUrl, localizeUrl } from "~/paraglide/runtime";
import { routeTree } from "./routeTree.gen";
import { isRetiredAutoApplyPath } from "~/lib/retired-auto-apply";
import { isPublicSeoLandingPath } from "~/lib/seo/public-landing-routes";

/**
 * Keep TanStack's route tree locale-neutral while exposing Paraglide's public
 * URL contract in rendered links and browser history. A bounded route-owned
 * exception supports German-only pages whose file route intentionally includes
 * `/de` because no untranslated counterpart exists.
 */
export const localeNavigationRewrite = {
  input: ({ url }) =>
    isPublicSeoLandingPath(url.pathname) || isRetiredAutoApplyPath(url.pathname)
      ? url
      : deLocalizeUrl(url),
  output: ({ url }) => {
    if (isPublicSeoLandingPath(url.pathname) || isRetiredAutoApplyPath(url.pathname)) return url;
    const localized = localizeUrl(url);

    // Paraglide's default wildcard produces `/de/` for the localized root.
    // TanStack's default trailing-slash policy and Alchema's public contract use
    // `/de`, so normalize only that root case without touching nested paths.
    if (url.pathname === "/" && localized.pathname !== "/") {
      localized.pathname = localized.pathname.replace(/\/$/, "");
    }
    return localized;
  },
} satisfies LocationRewrite;

export function getRouter() {
  const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    scrollRestoration: true,
    rewrite: localeNavigationRewrite,
  });
  return router;
}
